import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist'
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker?url'

GlobalWorkerOptions.workerSrc = pdfjsWorker

/**
 * Count the pages of a PDF file. Returns 1 for non-PDFs and on any read
 * failure, so callers can safely fall back to single-page behavior.
 */
export async function countPdfPages(file: File): Promise<number> {
  if (file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) return 1
  try {
    const data = await file.arrayBuffer()
    const pdf = await getDocument({ data }).promise
    const pages = pdf.numPages
    // Isolate cleanup: a destroy() rejection must not discard the page count
    // we already captured (otherwise we'd fall through to the catch and return
    // 1, silently reverting to the single-page bug this util fixes).
    try { await pdf.destroy() } catch { /* cleanup failure — page count already captured */ }
    return pages >= 1 ? pages : 1
  } catch {
    return 1
  }
}

/**
 * Upload properties that register a P&ID/PFD PDF as multi-page so the
 * backend fans out one parsing job per page (async-jobs-bridge gates on
 * total_pdf_pages > 1). Mirrors the region-modal flow's shape
 * (modalSlice.onModalSubmit); pdf_page_numbers lists every page since
 * no-modal uploads can't skip pages. Returns null for single-page files.
 */
export function multiPagePdfProperties(pageCount: number): Record<string, string> | null {
  if (pageCount <= 1) return null
  return {
    total_pdf_pages: String(pageCount),
    pdf_page_numbers: JSON.stringify(Array.from({ length: pageCount }, (_, i) => i + 1)),
  }
}
